import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../utils/AuthContext';
import { contentAPI } from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { 
  Sparkles, 
  Plus, 
  Loader2, 
  Save,
  Eye,
  ChevronDown,
  Briefcase,
  Heart,
  CheckCircle2,
  RefreshCw
} from 'lucide-react';

const PATH_ICONS = {
  spiritual: Sparkles,
  personal: Heart,
  professional: Briefcase
};

export default function ContentStudioPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [paths, setPaths] = useState({});
  const [selectedPath, setSelectedPath] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [lessonTitle, setLessonTitle] = useState('');
  const [keyPoints, setKeyPoints] = useState(['', '', '']);
  const [tone, setTone] = useState('warm');
  const [includeExercise, setIncludeExercise] = useState(true);
  const [includeReflection, setIncludeReflection] = useState(true);
  const [includeAssessment, setIncludeAssessment] = useState(false);
  
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [reviseNote, setReviseNote] = useState('');
  const [revising, setRevising] = useState(false);

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load learning paths
  useEffect(() => {
    const loadPaths = async () => {
      try {
        const response = await contentAPI.getPaths();
        setPaths(response.data);
      } catch (error) {
        console.error('Failed to load paths:', error);
      }
    };
    loadPaths();
  }, []);

  const handleKeyPointChange = (index, value) => {
    const newPoints = [...keyPoints];
    newPoints[index] = value;
    setKeyPoints(newPoints);
  };

  const addKeyPoint = () => {
    setKeyPoints([...keyPoints, '']);
  };

  const handleGenerate = async () => {
    if (!selectedPath || !selectedTopic || !lessonTitle) {
      toast.error('Please fill in path, topic, and lesson title');
      return;
    }

    const validKeyPoints = keyPoints.filter(p => p.trim());
    if (validKeyPoints.length === 0) {
      toast.error('Please add at least one key point');
      return;
    }

    setGenerating(true);
    setGeneratedContent(null);

    try {
      const response = await contentAPI.generateContent({
        topic: selectedTopic,
        path_category: selectedPath,
        lesson_title: lessonTitle,
        key_points: validKeyPoints,
        tone,
        include_exercise: includeExercise,
        include_reflection: includeReflection,
        include_assessment: includeAssessment
      });

      if (response.data.success) {
        setGeneratedContent(response.data.generated_content);
        toast.success('Content generated successfully!');
      } else {
        toast.error(response.data.error || 'Generation failed');
        if (response.data.raw_response) {
          setGeneratedContent({ raw_response: response.data.raw_response });
        }
      }
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedContent || generatedContent.raw_response) {
      toast.error('No valid content to save');
      return;
    }

    setSaving(true);
    try {
      const response = await contentAPI.saveDraft({
        title: generatedContent.title,
        description: generatedContent.description,
        content_type: 'article',
        topic: selectedTopic,
        path_category: selectedPath,
        content_body: generatedContent.content_body,
        has_exercise: includeExercise,
        exercise_content: generatedContent.exercise_content,
        reflection_prompts: generatedContent.reflection_prompts || [],
        capstone_goal: generatedContent.capstone_goal,
        has_assessment: includeAssessment,
        assessment_questions: generatedContent.assessment_questions || null
      });

      toast.success(`Lesson saved as #${response.data.lesson_number}!`);
      
      // Reset form
      setLessonTitle('');
      setKeyPoints(['', '', '']);
      setGeneratedContent(null);
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleRevise = async () => {
    if (!reviseNote.trim()) {
      toast.error('Tell CLaiR what to change');
      return;
    }

    setRevising(true);
    try {
      const response = await contentAPI.generateContent({
        topic: selectedTopic,
        path_category: selectedPath,
        lesson_title: lessonTitle,
        key_points: keyPoints.filter(p => p.trim()),
        tone,
        include_exercise: includeExercise,
        include_reflection: includeReflection,
        include_assessment: includeAssessment,
        revise_from: generatedContent.content_body,
        revise_note: reviseNote.trim()
      });

      if (response.data.success) {
        setGeneratedContent(response.data.generated_content);
        setReviseNote('');
        toast.success('Content revised!');
      } else {
        toast.error(response.data.error || 'Revision failed');
      }
    } catch (error) {
      toast.error('Failed to revise content');
    } finally {
      setRevising(false);
    }
  };

  if (!user?.is_admin) {
    return null;
  }

  const selectedPathData = paths[selectedPath];
  const topics = selectedPathData?.topics || [];

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-4xl mx-auto" data-testid="content-studio">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="text-primary" size={24} />
            <h1 className="text-3xl font-serif">CLaiR Content Studio</h1>
          </div>
          <p className="text-muted-foreground">Generate lesson content with AI assistance</p>
        </div>

        {/* Configuration */}
        <Card className="p-6 rounded-2xl space-y-6">
          <h2 className="text-xl font-medium">Lesson Setup</h2>
          
          {/* Path Selection */}
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(paths).map(([key, path]) => {
              const Icon = PATH_ICONS[key] || Sparkles;
              return (
                <button
                  key={key}
                  onClick={() => { setSelectedPath(key); setSelectedTopic(''); }}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    selectedPath === key 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`path-${key}`}
                >
                  <Icon size={20} className={selectedPath === key ? 'text-primary' : 'text-muted-foreground'} />
                  <p className="font-medium mt-2">{path.name}</p>
                  <p className="text-xs text-muted-foreground">{path.topics?.length || 0} topics</p>
                </button>
              );
            })}
          </div>

          {/* Topic Selection */}
          {selectedPath && (
            <div>
              <label className="text-sm font-medium mb-2 block">Topic</label>
              <select
                value={selectedTopic}
                onChange={(e) => setSelectedTopic(e.target.value)}
                className="w-full p-3 rounded-lg border border-border bg-background"
                data-testid="topic-select"
              >
                <option value="">Select a topic...</option>
                {topics.map(topic => (
                  <option key={topic.slug} value={topic.slug}>{topic.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Lesson Title */}
          <div>
            <label className="text-sm font-medium mb-2 block">Lesson Title</label>
            <Input
              value={lessonTitle}
              onChange={(e) => setLessonTitle(e.target.value)}
              placeholder="e.g., Discovering Your Professional Values"
              data-testid="lesson-title"
            />
          </div>

          {/* Key Points */}
          <div>
            <label className="text-sm font-medium mb-2 block">Key Points to Cover</label>
            <div className="space-y-2">
              {keyPoints.map((point, index) => (
                <Input
                  key={`topic-${index}`}
                  value={point}
                  onChange={(e) => handleKeyPointChange(index, e.target.value)}
                  placeholder={`Key point ${index + 1}`}
                  data-testid={`key-point-${index}`}
                />
              ))}
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={addKeyPoint}
              className="mt-2"
            >
              <Plus size={16} className="mr-1" /> Add point
            </Button>
          </div>

          {/* Options */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeExercise}
                onChange={(e) => setIncludeExercise(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Include exercise</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeReflection}
                onChange={(e) => setIncludeReflection(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Include reflection prompts</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeAssessment}
                onChange={(e) => setIncludeAssessment(e.target.checked)}
                className="rounded"
                data-testid="include-assessment-checkbox"
              />
              <span className="text-sm">Include assessment</span>
            </label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="p-2 rounded border border-border bg-background text-sm"
            >
              <option value="warm">Warm & Supportive</option>
              <option value="professional">Professional & Clear</option>
              <option value="motivational">Motivational & Energizing</option>
            </select>
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={generating || !selectedPath || !selectedTopic || !lessonTitle}
            className="w-full rounded-full"
            size="lg"
            data-testid="generate-btn"
          >
            {generating ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                CLaiR is writing...
              </>
            ) : (
              <>
                <Sparkles size={18} className="mr-2" />
                Generate with CLaiR
              </>
            )}
          </Button>
        </Card>

        {/* Generated Content Preview */}
        {generatedContent && (
          <Card className="p-6 rounded-2xl space-y-6" data-testid="generated-content">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-medium">Generated Content</h2>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full"
                  onClick={() => setShowFullPreview(!showFullPreview)}
                  data-testid="preview-btn"
                >
                  <Eye size={16} className="mr-1" /> {showFullPreview ? 'Collapse' : 'Preview'}
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving || generatedContent.raw_response}
                  size="sm"
                  className="rounded-full"
                  data-testid="save-btn"
                >
                  {saving ? (
                    <Loader2 size={16} className="mr-1 animate-spin" />
                  ) : (
                    <Save size={16} className="mr-1" />
                  )}
                  Save to Library
                </Button>
              </div>
            </div>

            {generatedContent.raw_response ? (
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Raw response (needs manual formatting):</p>
                <pre className="text-xs overflow-auto max-h-96">{generatedContent.raw_response}</pre>
              </div>
            ) : (
              <>
                {/* Title & Description */}
                <div>
                  <h3 className="text-lg font-medium">{generatedContent.title}</h3>
                  <p className="text-muted-foreground">{generatedContent.description}</p>
                </div>

                {/* Content Preview */}
                <div className={`bg-muted/30 p-4 rounded-lg overflow-auto ${showFullPreview ? '' : 'max-h-64'}`}>
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown>
                      {showFullPreview ? generatedContent.content_body : generatedContent.content_body?.substring(0, 1000) + '...'}
                    </ReactMarkdown>
                  </div>
                </div>

                {/* Exercise */}
                {generatedContent.exercise_content && (
                  <div className="bg-accent/10 p-4 rounded-lg">
                    <p className="font-medium mb-2 flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-accent" />
                      Exercise
                    </p>
                    <div className="prose prose-sm max-w-none text-muted-foreground">
                      <ReactMarkdown>
                        {showFullPreview ? generatedContent.exercise_content : generatedContent.exercise_content?.substring(0, 300) + '...'}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Reflection Prompts */}
                {generatedContent.reflection_prompts?.length > 0 && (
                  <div>
                    <p className="font-medium mb-2">Reflection Prompts</p>
                    <ul className="space-y-1">
                      {generatedContent.reflection_prompts.map((prompt, i) => (
                        <li key={`prompt-${i}`} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="text-primary">•</span>
                          {prompt}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Assessment Preview */}
                {generatedContent.assessment_questions?.length > 0 && (
                  <div className="border border-accent/30 rounded-lg p-4 space-y-4">
                    <p className="font-medium flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-accent" />
                      Assessment ({generatedContent.assessment_questions.length} questions)
                    </p>
                    {generatedContent.assessment_questions.map((q, qi) => (
                      <div key={`aq-${qi}-${q.question_text?.slice(0,20)}`} className="bg-muted/30 p-3 rounded-lg space-y-2">
                        <p className="text-sm font-medium">{qi + 1}. {q.question_text}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${q.type === 'knowledge_check' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {q.type === 'knowledge_check' ? 'Knowledge Check' : 'Self-Assessment'}
                        </span>
                        <ul className="space-y-1 mt-1">
                          {q.choices?.map((c, ci) => (
                            <li key={`aq-${qi}-c-${ci}`} className={`text-xs flex items-start gap-2 ${c.is_correct ? 'text-green-600 font-medium' : 'text-muted-foreground'}`}>
                              <span>{c.is_correct ? '✓' : '○'}</span>
                              <span>{c.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {/* Revise Section */}
                <div className="border-t border-border pt-4 space-y-3">
                  <p className="text-sm font-medium">Want changes? Tell CLaiR what to revise:</p>
                  <div className="flex gap-2">
                    <Input
                      value={reviseNote}
                      onChange={(e) => setReviseNote(e.target.value)}
                      placeholder="e.g., Make the tone more conversational, add a real-world example, shorten the intro..."
                      data-testid="revise-input"
                      onKeyDown={(e) => e.key === 'Enter' && !revising && handleRevise()}
                    />
                    <Button
                      onClick={handleRevise}
                      disabled={revising || !reviseNote.trim()}
                      className="rounded-full flex-shrink-0"
                      data-testid="revise-btn"
                    >
                      {revising ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>
                          <RefreshCw size={16} className="mr-1" />
                          Revise
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
